import { computed, unref } from 'vue';
import { useRoute } from 'vue-router';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import { useEnabledTypes } from '@/app/community/composables/useEnabledTypes';
import { useCommunityStore } from '@/stores/community';
import { useCommunityInteractionRulesStore } from '@/stores/community-interaction-rules';
import { useIdentityStore } from '@/stores/identity';
import {
  COMMUNITY_INTERACTION_POST_FILTER_OPTIONS,
  COMMUNITY_ROUTE_NAMES,
  DELETING_TAG_TOASTS,
  MAX_RECENT_USERS_FILTER_LIST,
  removeCommunityTagConfirmMessage,
} from '@/app/community/constants';
import {
  MESSAGE_TAG_FILTER_TYPE_OPTIONS,
  CHANNEL_FILTER_INTERACTION_TYPE_MAP,
  FILTER_DETAILS,
  FILTER_QUERY_PARAMS,
  INTERACTION_TYPE_MAP,
  MESSAGE_CONTENT_FILTER_FLAG_TYPE_OPTIONS,
  MESSAGE_CONTENT_FILTER_OPTIONS,
  MESSAGE_STATE_FILTER_OPTIONS,
  MESSAGE_TYPE_FILTER_OPTIONS,
  SENTIMENT_FILTER_OPTIONS,
  VIEW_FILTER_QUERY_PARAMS,
} from '@/app/community/constants/communityFilters';
import dayjs from 'dayjs';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import mapValues from 'lodash/mapValues';
import humps from 'humps';
import { communityRouteName } from '@/app/community/routes';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import { useCommunityMessageTagsStore } from '@/stores/community-message-tags';
import { useCommunityInboxViewStore } from '@/stores/community-inbox-view';
import cloneDeep from 'lodash/cloneDeep';
import { useConfirmationDialog, useFilterSidePanel, useToast } from '@dashhudson/dashing-ui';
import isFunction from 'lodash/isFunction';
import { getTagOperatorAndValues } from '@/app/community/composables/utils/composable-utils';
import uniq from 'lodash/uniq';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

export function useCommunityFilters(communityFilters = {}, composableOptions = {}) {
  const { ignoreInteractionPageConstraints = false } = composableOptions;
  const identityStore = useIdentityStore();
  const communityStore = useCommunityStore();
  const communityInteractionRulesStore = useCommunityInteractionRulesStore();
  const communityMultiBrandsStore = useCommunityMultiBrandsStore();
  const messageTagStore = useCommunityMessageTagsStore();
  const inboxViewStore = useCommunityInboxViewStore();
  const route = useRoute();
  const isCustomViewRoute = computed(() => route.meta?.customViewRoute);
  const {
    brandCanAccessInternalNotes,
    brandCanAccessInboxAutomation,
    canAccessCommunityOverview,
    canAccessSentimentAnalysis,
    canAccessMultiBrandInbox,
  } = useCommunityPermissions();
  const { enabledPlatforms } = useEnabledTypes();
  const { closeFilter, openFilter } = useFilterSidePanel();
  const { showConfirmationDialog } = useConfirmationDialog({
    group: 'global',
    onAccept: null,
    onReject: null,
  });
  const { addToast } = useToast({
    closable: true,
    life: 3000,
  });

  const filterMap = FILTER_DETAILS.COMMUNITY;
  const emptyCheck = (q) => (isEmpty(q) && typeof q === 'object') || q === undefined;

  /**
   * Current page check
   */
  const isCommunityRoot = computed(() => route?.name === communityRouteName);
  const viewInsightsWithAccess = computed(
    () => canAccessCommunityOverview && route?.name === COMMUNITY_ROUTE_NAMES.INSIGHTS,
  );
  const viewTopPostsWithAccess = computed(
    () => canAccessCommunityOverview && route?.name === COMMUNITY_ROUTE_NAMES.TOP_POSTS,
  );
  const viewInternalNotesWithAccess = computed(
    () => brandCanAccessInternalNotes && route?.name === COMMUNITY_ROUTE_NAMES.INTERNAL_NOTES,
  );
  const isCustomViewPage = computed(() => {
    const routeViewId = route?.params?.viewId ? parseInt(route.params.viewId, 10) : null;
    return routeViewId !== null && routeViewId === inboxViewStore.activeView?.id;
  });
  const checkCurrentPage = (name) => route?.name === name;

  /**
   * Filters and filter options
   */
  const internalFilters = computed(() =>
    mapValues(communityFilters.value, (filter) => unref(filter)),
  );
  const viewBrandIds = computed(() => {
    const selectedBrandIds = inboxViewStore.activeViewFilters?.brandIds;
    return selectedBrandIds ? selectedBrandIds.split(',').map((bid) => parseFloat(bid)) : [];
  });
  const hasTextAnalysisAccessibleBrands = computed(() => {
    if (canAccessMultiBrandInbox.value) {
      const accessibleBrandIds = isCustomViewPage.value
        ? viewBrandIds.value
        : communityMultiBrandsStore.currentRouteAccessibleBrandIds;
      const enabledBrandIds = communityMultiBrandsStore.textAnalysisAccessibleBrandIds;
      return (
        enabledBrandIds.length > 0 && accessibleBrandIds.some((id) => enabledBrandIds.includes(id))
      );
    }
    return canAccessSentimentAnalysis.value;
  });
  const hasAutomationAccessibleBrands = computed(() => {
    if (canAccessMultiBrandInbox.value) {
      const accessibleBrandIds = isCustomViewPage.value
        ? viewBrandIds.value
        : communityMultiBrandsStore.currentRouteAccessibleBrandIds;
      const enabledBrandIds = communityMultiBrandsStore.interactionRuleAccessibleBrandIds;
      return (
        enabledBrandIds.length > 0 && accessibleBrandIds.some((id) => enabledBrandIds.includes(id))
      );
    }
    return brandCanAccessInboxAutomation.value;
  });
  const filtersAvailability = computed(() => ({
    sentiments: hasTextAnalysisAccessibleBrands.value,
    interactionRuleIds: !viewInternalNotesWithAccess.value && hasAutomationAccessibleBrands.value,
    assigneeIds:
      !viewInternalNotesWithAccess.value &&
      !checkCurrentPage(COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU),
    mentionedUserIds: viewInternalNotesWithAccess.value,
    brandIds: canAccessMultiBrandInbox.value,
    brandTagIds: canAccessMultiBrandInbox.value,
    sort: !viewInsightsWithAccess.value,
    messageContent: hasTextAnalysisAccessibleBrands.value,
  }));
  const filterOptionsAvailability = computed(() => ({
    isArchived: viewInsightsWithAccess.value,
    isFollowUp: !viewInternalNotesWithAccess.value || ignoreInteractionPageConstraints,
    isRead: true,
    responseRecommended:
      hasTextAnalysisAccessibleBrands.value &&
      (!viewInternalNotesWithAccess.value || ignoreInteractionPageConstraints),
  }));
  function shouldShowFilter(filter) {
    const availability = { ...filtersAvailability.value, ...filterOptionsAvailability.value };
    if (
      !ignoreInteractionPageConstraints &&
      checkCurrentPage(COMMUNITY_ROUTE_NAMES.INTERNAL_NOTES)
    ) {
      const internalNotesFilters = [
        'mentionedUserIds',
        'isRead',
        'startDate',
        'endDate',
        'platforms',
        'messageTypes',
        'brandIds',
      ];
      return internalNotesFilters.includes(filter) && (availability[filter] ?? true);
    }
    return availability[filter] ?? true;
  }

  const recentlyAssignedUsers = computed(() => {
    if (canAccessMultiBrandInbox.value) {
      return communityMultiBrandsStore.assignedUsersForReportingPeriod;
    }
    const assigneeFilterOptions = communityStore.mostRecentlyAssignedUsers;
    return (assigneeFilterOptions ?? []).slice(0, MAX_RECENT_USERS_FILTER_LIST + 1);
  });

  const recentlyMentionedUsers = computed(() => {
    const mentionedUserFilterOptions = canAccessMultiBrandInbox.value
      ? communityMultiBrandsStore.mostRecentlyMentionedUsers
      : communityStore.mostRecentlyMentionedUsers;
    return (mentionedUserFilterOptions ?? []).slice(0, MAX_RECENT_USERS_FILTER_LIST + 1);
  });

  async function onDeleteTag(tagId) {
    closeFilter();
    await showConfirmationDialog({
      header: 'Remove Tag For All Messages?',
      acceptLabel: 'Delete',
      acceptSeverity: 'danger',
      message: removeCommunityTagConfirmMessage,
    }).then(async (accepted) => {
      if (accepted) {
        try {
          await messageTagStore.deleteTag(tagId);
          addToast({
            message: DELETING_TAG_TOASTS.SUCCESS,
            severity: 'success',
          });
        } catch {
          addToast({
            message: DELETING_TAG_TOASTS.FAILED,
            severity: 'error',
          });
        }
      }
    });

    openFilter();
  }

  function formatFilterOptions(options, removeCallback, sortByLabel = false) {
    if (options?.length) {
      const formatted = options.map((option) => {
        if (option.type === 'line') {
          return { divider: true };
        }
        let label = option.label ?? option.name;
        const value = option.value ?? option.id;
        if (option?.note) {
          label += ` ${option.note}`;
        }
        if (isFunction(removeCallback)) {
          if (option?.children) {
            option.children.forEach((child) => {
              child.removeCallback = () => removeCallback(child);
            });
          } else {
            option.removeCallback = () => removeCallback(option);
          }
        }
        return { ...option, label, value };
      });
      if (sortByLabel) {
        return sortBy(formatted, (o) => o?.label?.toLowerCase());
      }
      return formatted;
    }
    return [];
  }
  const selectedPlatforms = computed(() =>
    internalFilters.value?.platforms?.length
      ? internalFilters.value?.platforms
      : enabledPlatforms.value || [],
  );
  const disabledMessageTypes = computed(() => {
    const disabledTypes = [];
    Object.values(MESSAGE_TYPE_FILTER_OPTIONS).forEach((option) => {
      const platformList = viewInsightsWithAccess.value
        ? (option.insightsPlatforms ?? option.platforms)
        : option.platforms;
      if (platformList.every((platform) => !selectedPlatforms.value.includes(platform))) {
        disabledTypes.push(option.value);
      }
    });
    return disabledTypes;
  });
  const messageTypesFilterOptions = computed(() => {
    const defaultOptions = filterMap.options.messageTypes;
    const options = !viewInternalNotesWithAccess.value
      ? defaultOptions
      : defaultOptions.filter((t) => !t?.subtype);
    return options.map((option) => {
      const info = viewInsightsWithAccess.value
        ? (option.insightsInfo ?? option.info)
        : option.info;
      return {
        ...option,
        info,
        disabled: disabledMessageTypes.value.includes(option.value),
      };
    });
  });
  const dmMessageTypeOnlySelected = computed(() => {
    const selectedMessageTypes = internalFilters.value?.messageTypes;
    return (
      selectedMessageTypes?.length === 1 &&
      selectedMessageTypes[0] === MESSAGE_TYPE_FILTER_OPTIONS.CONVERSATION.value
    );
  });
  const messageStateFilterOptions = computed(() => {
    const options = filterMap.options.messageState;
    return options
      .filter((option) => filterOptionsAvailability.value[option.queryKey])
      .sort((a, b) => a.label.localeCompare(b.label));
  });
  const tagOptions = computed(() => {
    const removeCallback = (option) => onDeleteTag(option.id);
    if (canAccessMultiBrandInbox.value) {
      return formatFilterOptions(
        communityMultiBrandsStore.currentRouteAccessibleMessageTags,
        removeCallback,
        true,
      );
    }
    return formatFilterOptions(
      messageTagStore.getSelectedBrandTags(identityStore.currentBrand.id),
      removeCallback,
    );
  });
  const messageRuleFilterOptions = computed(() => {
    if (canAccessMultiBrandInbox.value) {
      return formatFilterOptions(
        communityMultiBrandsStore.currentRouteAccessibleInteractionRules,
        undefined,
        true,
      );
    }
    return formatFilterOptions(communityInteractionRulesStore.interactionRules);
  });
  const textAnalysisAccessRequiredFiltersSelected = computed(
    () =>
      internalFilters.value?.sentiments?.length > 0 ||
      internalFilters.value?.messageContent?.length > 0 ||
      internalFilters.value?.messageState?.includes(
        MESSAGE_STATE_FILTER_OPTIONS.RESPONSE_RECOMMENDED.value,
      ),
  );
  const automationAccessRequiredFiltersSelected = computed(
    () => internalFilters.value?.interactionRuleIds?.length > 0,
  );
  const brandFilterOptions = computed(() => {
    const options = [...communityMultiBrandsStore.currentRouteAccessibleBrands].map((brand) => {
      const disabled =
        (!brand?.permissions?.community?.can_access_text_analysis &&
          textAnalysisAccessRequiredFiltersSelected.value) ||
        (!brand?.permissions?.community?.can_access_inbox_automation &&
          automationAccessRequiredFiltersSelected.value);
      return { ...brand, label: brand.name, disabled };
    });
    return formatFilterOptions(options, undefined, true);
  });

  function formatDateQueryParams(startDate, endDate) {
    if (startDate || endDate) {
      const end = endDate ? dayjs(endDate) : dayjs();
      const start = startDate ? dayjs(startDate) : dayjs(end).subtract(1, 'month');
      return {
        range: { start: start.startOf('day').toDate(), end: end.endOf('day').toDate() },
      };
    }
    return {};
  }
  function parseToFilter(value, type, elementType) {
    if (value === undefined) {
      return type === Array ? [] : undefined;
    }
    if (type && value instanceof type) {
      return value;
    }
    if (type && type === Boolean && typeof value === 'boolean') {
      return value;
    }
    switch (type) {
      case Boolean:
        return value === 'true';
      case Number:
        return parseFloat(value);
      case Array:
        return String(value)
          .split(',')
          .map((el) => parseToFilter(el, elementType));
      default:
        return value;
    }
  }

  const filterOptions = computed(() => ({
    ...filterMap.options,
    tagIds: tagOptions.value,
    interactionRuleIds: messageRuleFilterOptions.value,
    assigneeId: formatFilterOptions(recentlyAssignedUsers.value),
    mentionedUserIds: formatFilterOptions(recentlyMentionedUsers.value).filter(
      (o) => o.value !== -1,
    ),
    platforms: enabledPlatforms.value,
    messageTypes: messageTypesFilterOptions.value,
    messageState: messageStateFilterOptions.value,
    brandIds: brandFilterOptions.value,
    brandTagIds: formatFilterOptions(communityMultiBrandsStore.currentRouteAccessibleBrandTags),
  }));

  function sanitizeValue(key, parsedValue) {
    const options = filterOptions.value?.[key];
    if (options && parsedValue) {
      if (Array.isArray(parsedValue)) {
        const hasChildren = options.find((o) => o?.children?.length > 0);
        if (hasChildren) {
          // Tree type filter options (ex. Multi Brand Message Tags)
          return parsedValue.filter((v) =>
            options.some((o) => o.children.some((oc) => String(oc.value) === String(v))),
          );
        }
        return parsedValue.filter((v) => options.some((o) => String(o?.value ?? o) === String(v)));
      }
      const selectedOption = options.find((o) => String(o?.value ?? o) === String(parsedValue));
      return selectedOption?.value ?? selectedOption;
    }
    return parsedValue;
  }
  function parseToFilters(queryParams = {}, isViewFilters = false, shouldSanitize = true) {
    if (!isEmpty(queryParams)) {
      const rawQueryParams = cloneDeep(queryParams);
      if (rawQueryParams.tagIds) {
        const { values, operator } = getTagOperatorAndValues(rawQueryParams.tagIds);
        rawQueryParams.tagOperator = operator;
        rawQueryParams.tagIds = values;
      }
      const parsedQuery = mapValues({ ...humps.camelizeKeys(rawQueryParams) }, (value, key) => {
        const { type, elementType } =
          (isViewFilters ? VIEW_FILTER_QUERY_PARAMS[key] : FILTER_QUERY_PARAMS[key]) ?? {};
        if (!type) {
          return undefined;
        }
        const parsedValue = parseToFilter(value, type, elementType);
        return shouldSanitize ? sanitizeValue(key, parsedValue) : parsedValue;
      });
      if (isViewFilters) {
        return parsedQuery;
      }
      const { startDate, endDate, isRead, isFollowUp, isArchived, responseRecommended, ...params } =
        parsedQuery;
      const messageStateOptions = Object.values(MESSAGE_STATE_FILTER_OPTIONS).filter(
        (option) => parsedQuery[option.queryKey] === option.queryValue,
      );
      const overrides = {};
      if (startDate && endDate) {
        overrides.sourceUpdated = formatDateQueryParams(startDate, endDate);
      }
      overrides.messageState = messageStateOptions.map((option) => option.value);

      // Avoid overriding the stored brand filter with an empty query param
      if (params?.brandIds && !params?.brandIds?.length) {
        delete params.brandIds;
      }
      if (params?.brandTagIds && !params?.brandTagIds?.length) {
        delete params.brandTagIds;
      }
      return { ...params, ...overrides };
    }
    return {};
  }
  function parseViewFilterQueryToFilters(viewFilters = {}, shouldSanitize = true) {
    if (!isEmpty(viewFilters)) {
      const {
        types = [],
        isPositive,
        isNeutral,
        isNegative,
        isSpam,
        isQuestion,
        ...filters
      } = parseToFilters(viewFilters, true, shouldSanitize);
      const parsedViewFilters = { ...cloneDeep(filterMap.default), ...filters };

      if (types.length) {
        parsedViewFilters.platforms = uniq(types.map((t) => INTERACTION_TYPE_MAP[t].platform));

        // Derive regular message types from interaction types
        parsedViewFilters.messageTypes = uniq(
          types
            .filter((mt) => mt && !MESSAGE_TYPE_FILTER_OPTIONS[mt]?.subtype)
            .map((t) => INTERACTION_TYPE_MAP[t].messageType),
        );
      }

      if (isPositive) {
        parsedViewFilters.sentiments.push(SENTIMENT_FILTER_OPTIONS.POSITIVE.value);
      }
      if (isNeutral) {
        parsedViewFilters.sentiments.push(SENTIMENT_FILTER_OPTIONS.NEUTRAL.value);
      }
      if (isNegative) {
        parsedViewFilters.sentiments.push(SENTIMENT_FILTER_OPTIONS.NEGATIVE.value);
      }
      if (isSpam) {
        parsedViewFilters.messageContent.push(MESSAGE_CONTENT_FILTER_OPTIONS.SPAM.value);
      }
      if (isQuestion) {
        parsedViewFilters.messageContent.push(MESSAGE_CONTENT_FILTER_OPTIONS.QUESTION.value);
      }
      return parsedViewFilters;
    }
    return cloneDeep(filterMap.default);
  }

  const applyListConstraint = (options, allowedValues) =>
    options.map((option) => {
      if (option.divider) return option;
      const value = option.value || option;
      const isAllowed = allowedValues.includes(value);
      if (typeof option === 'string') {
        return { value: option, disabled: !isAllowed };
      }
      return { ...option, disabled: option.disabled || !isAllowed };
    });

  const parsedViewFilters = computed(() => {
    const viewFilters = inboxViewStore.activeViewFilters;
    if (!(inboxViewStore.activeView && isCustomViewRoute.value) || !viewFilters) {
      return null;
    }
    return parseViewFilterQueryToFilters(viewFilters, false);
  });

  const constrainedSentiments = computed(() => {
    if (!parsedViewFilters.value?.sentiments?.length) {
      return filterMap.options.sentiments;
    }
    return applyListConstraint(filterMap.options.sentiments, parsedViewFilters.value.sentiments);
  });

  const constrainedAssigneeOptions = computed(() => {
    const baseOptions = formatFilterOptions(recentlyAssignedUsers.value);
    if (!parsedViewFilters.value?.assigneeIds?.length) {
      return baseOptions;
    }
    return applyListConstraint(baseOptions, parsedViewFilters.value.assigneeIds);
  });

  const constrainedMessageContent = computed(() => {
    if (!parsedViewFilters.value?.messageContent?.length) {
      return filterMap.options.messageContent;
    }
    return applyListConstraint(
      filterMap.options.messageContent,
      parsedViewFilters.value.messageContent,
    );
  });

  const constrainedPlatforms = computed(() => {
    const basePlatforms = filterMap.options.platforms.filter((platform) =>
      enabledPlatforms.value.includes(platform),
    );
    if (!parsedViewFilters.value?.platforms?.length) {
      return basePlatforms.map((platform) => ({
        value: platform,
        disabled: false,
      }));
    }
    return applyListConstraint(basePlatforms, parsedViewFilters.value.platforms);
  });

  const constrainedMessageTypes = computed(() => {
    if (!parsedViewFilters.value?.messageTypes?.length) {
      return messageTypesFilterOptions.value;
    }
    return applyListConstraint(
      messageTypesFilterOptions.value,
      parsedViewFilters.value.messageTypes,
    );
  });

  const constrainedTagOperator = computed(() => {
    if (!parsedViewFilters.value?.tagIds?.length) {
      return filterMap.options.tagOperator;
    }
    const tagOperator = parsedViewFilters.value.tagOperator;
    return Object.values(MESSAGE_TAG_FILTER_TYPE_OPTIONS).map((o) => ({
      ...o,
      disabled: o.value !== tagOperator,
    }));
  });

  const constrainedTagIds = computed(() => {
    const allowedBrandIds = parsedViewFilters.value?.brandIds;
    if (!allowedBrandIds?.length) {
      return tagOptions.value;
    }

    const allowedTagIds = parsedViewFilters.value?.tagIds;
    const selectedBrandTagOptions = tagOptions.value.filter((group) => {
      const brandId =
        typeof group.value === 'string' && group.value.includes('_')
          ? parseInt(group.value.split('_')[1], 10)
          : parseInt(group.value, 10);
      return allowedBrandIds.includes(brandId);
    });
    if (!allowedTagIds?.length) {
      return selectedBrandTagOptions;
    }

    const tagOperator = parsedViewFilters.value.tagOperator;
    return selectedBrandTagOptions.map((group) => {
      let brandParentDisabled = true;
      let children = [];
      if (tagOperator === 'OR') {
        children = applyListConstraint(group.children, allowedTagIds);
        if (children.some((child) => !child.disabled)) {
          brandParentDisabled = false;
        }
      } else {
        children = applyListConstraint(group.children, []);
        if (children.some((child) => allowedTagIds.includes(child.value))) {
          brandParentDisabled = false;
        }
      }
      return {
        ...group,
        children,
        disabled: brandParentDisabled,
      };
    });
  });

  const constrainedBrandIds = computed(() => {
    const baseOptions = brandFilterOptions.value;
    if (!parsedViewFilters.value?.brandIds?.length) {
      return baseOptions;
    }
    const allowedBrandIds = parsedViewFilters.value?.brandIds;
    const selectedBrandOptions = baseOptions.filter((b) => allowedBrandIds.includes(b.value));
    const constrained = applyListConstraint(selectedBrandOptions, allowedBrandIds);

    const inaccessibleViewBrands = allowedBrandIds
      .filter((brandId) => !baseOptions.some((o) => o.value === brandId))
      .map((brandId) => {
        const brandData = inboxViewStore.activeView?.viewBrandDetails?.find(
          (brand) => brand.id === brandId,
        );
        return {
          value: brandId,
          label: brandData?.name || `Brand ${brandId}`,
          avatarUrl: brandData?.avatarUrl || brandData?.avatar_url,
          disabled: true,
          tooltip: "This brand is included in the Custom View but you don't have access",
        };
      });

    return [...constrained, ...inaccessibleViewBrands];
  });

  const constrainedRuleFilterOptions = computed(() => {
    const allowedBrandIds = parsedViewFilters.value?.brandIds;
    if (!allowedBrandIds?.length) {
      return messageRuleFilterOptions.value;
    }

    return messageRuleFilterOptions.value.filter((group) => {
      const brandId =
        typeof group.value === 'string' && group.value.includes('_')
          ? parseInt(group.value.split('_')[1], 10)
          : parseInt(group.value, 10);
      return allowedBrandIds.includes(brandId);
    });
  });

  const constrainedFilterOptions = computed(() => ({
    ...filterMap.options,
    tagIds: constrainedTagIds.value,
    tagOperator: constrainedTagOperator.value,
    interactionRuleIds: constrainedRuleFilterOptions.value,
    assigneeId: constrainedAssigneeOptions.value,
    mentionedUserIds: formatFilterOptions(recentlyMentionedUsers.value).filter(
      (o) => o.value !== -1,
    ),
    platforms: constrainedPlatforms.value,
    messageTypes: constrainedMessageTypes.value,
    messageState: messageStateFilterOptions.value,
    messageContent: constrainedMessageContent.value,
    sentiments: constrainedSentiments.value,
    brandIds: constrainedBrandIds.value,
    brandTagIds: formatFilterOptions(communityMultiBrandsStore.currentRouteAccessibleBrandTags),
  }));

  const postFilterWidgetOptions = computed(() => {
    const sources = selectedPlatforms.value.map(
      (p) => COMMUNITY_INTERACTION_POST_FILTER_OPTIONS[p],
    );
    const all = { ...COMMUNITY_INTERACTION_POST_FILTER_OPTIONS.ALL };
    all.value = sources.map((s) => s.value).join(',');

    return {
      source: sources.length > 1 ? all.value : sources[0].value,
      sourceOptions: sources.length > 1 ? [all, ...sources] : sources,
      sourceSelectDisabled: sources.length === 1,
    };
  });

  function parseToQuery(appliedFilters = {}) {
    if (!isEmpty(appliedFilters)) {
      const { sourceUpdated, messageState, tagIds, tagOperator, ...filters } = appliedFilters;
      const overrides = {};
      if (tagIds?.length) {
        overrides.tagIds = `${tagOperator ?? 'OR'}:${tagIds.join(',')}`;
      }
      if (sourceUpdated?.range) {
        overrides.startDate = dayjs(sourceUpdated.range.start).format('YYYY-MM-DD');
        overrides.endDate = dayjs(sourceUpdated.range.end).format('YYYY-MM-DD');
      }
      if (messageState?.length) {
        messageState.forEach((ms) => {
          const state = MESSAGE_STATE_FILTER_OPTIONS[ms];
          overrides[state.queryKey] = state.queryValue;
        });
      }
      return omitBy({ ...filters, ...overrides }, emptyCheck);
    }
    return {};
  }
  const defaultQueryValue = computed(() => ({
    mentionedUserIds: String(identityStore.identity.id),
  }));
  function getQueryParamOverrides(currentQuery) {
    const merged = {
      ...currentQuery,
      ...filtersAvailability.value,
      ...filterOptionsAvailability.value,
    };
    const overrides = mapValues(merged, (value, key) =>
      shouldShowFilter(key) ? (currentQuery[key] ?? defaultQueryValue.value[key]) : undefined,
    );
    return { ...currentQuery, ...overrides };
  }
  function getTypesFromMessageTypes(messageTypes) {
    if (!isEmpty(messageTypes)) {
      const selectedTypes =
        messageTypes
          // exclude subtype
          ?.filter((m) => !MESSAGE_TYPE_FILTER_OPTIONS[m].subtype)
          ?.flatMap((m) => [...MESSAGE_TYPE_FILTER_OPTIONS[m].includes]) ?? [];
      return uniq(selectedTypes);
    }
    return [];
  }
  function getTypesFromPlatforms(platforms) {
    const platformValues = platforms?.length ? platforms : enabledPlatforms.value;
    const selectedTypes =
      platformValues?.flatMap((p) => [...CHANNEL_FILTER_INTERACTION_TYPE_MAP[p]]) ?? [];
    return uniq(selectedTypes);
  }
  function buildViewFilterQuery(viewFilters = {}) {
    if (!isEmpty(viewFilters)) {
      const { brandIds, sentiments, messageTypes, platforms, messageContent, ...filters } =
        viewFilters;
      const parsed = parseToQuery(filters);

      // brand
      parsed.brandIds = brandIds?.length ? brandIds : brandFilterOptions.value.map((b) => b.id);

      // sentiment
      if (sentiments?.length) {
        sentiments.forEach((s) => {
          const key = SENTIMENT_FILTER_OPTIONS[s].field;
          parsed[key] = true;
        });
      }

      // type
      const selectedPlatformTypes = getTypesFromPlatforms(platforms);
      const selectedMessageTypes = getTypesFromMessageTypes(messageTypes);

      let types = selectedPlatformTypes;
      if (selectedMessageTypes?.length) {
        // Message types selected: intersect with platform types
        types = intersection(selectedPlatformTypes, selectedMessageTypes);
      }
      parsed.types = types;

      // message content
      parsed.messageContent = messageContent?.filter(
        (c) => !MESSAGE_CONTENT_FILTER_FLAG_TYPE_OPTIONS.includes(c),
      );
      parsed.isSpam =
        messageContent?.includes(MESSAGE_CONTENT_FILTER_OPTIONS.SPAM.value) || undefined;
      parsed.isQuestion =
        messageContent?.includes(MESSAGE_CONTENT_FILTER_OPTIONS.QUESTION.value) || undefined;

      // remove empty fields
      const viewFilterQuery = omitBy(parsed, emptyCheck);
      return mapValues(viewFilterQuery, (value) =>
        Array.isArray(value) ? value.join(',') : value,
      );
    }
    return {};
  }
  function getAppliedViewFiltersDiff(viewFilters = {}, appliedFilters = {}) {
    const result = {};

    Object.keys(appliedFilters).forEach((key) => {
      const appliedValue = appliedFilters[key];
      const viewValue = viewFilters[key];
      let isDiff;
      if (Array.isArray(appliedValue) && Array.isArray(viewValue)) {
        isDiff = !isEqual(appliedValue.sort(), viewValue.sort());
      } else {
        isDiff = !isEqual(appliedValue, viewValue);
      }
      if (isDiff) {
        result[key] = appliedValue;
      }
    });

    return result;
  }

  return {
    isCommunityRoot,
    viewInsightsWithAccess,
    viewTopPostsWithAccess,
    viewInternalNotesWithAccess,
    filtersAvailability,
    filterOptionsAvailability,
    messageStateFilterOptions,
    filterOptions,
    constrainedFilterOptions,
    disabledMessageTypes,
    dmMessageTypeOnlySelected,
    enabledPlatforms,
    postFilterWidgetOptions,
    parsedViewFilters,
    emptyCheck,
    shouldShowFilter,
    parseToFilter,
    parseToFilters,
    parseToQuery,
    parseViewFilterQueryToFilters,
    getQueryParamOverrides,
    buildViewFilterQuery,
    getAppliedViewFiltersDiff,
    formatFilterOptions,
  };
}
